/**
 * AirCard — air quality + pollen card. Self-hides when air is clean.
 *
 * Source: Open-Meteo Air Quality endpoint
 *   https://air-quality-api.open-meteo.com/v1/air-quality
 *   ?latitude=…&longitude=…&current=european_aqi,pm2_5,pm10,uv_index
 *
 * Behavior:
 *   - Fetches AQI on first render with the user's coords (cached 30 min).
 *   - If AQI < 60 (good/fair) → widget renders a tiny green dot only.
 *     Anticipation engine score < threshold so it stays low in the feed.
 *   - If AQI ≥ 100 (poor / very poor) → widget renders large warning card
 *     with PM2.5 + recommendation ("limit outdoor exposure", "wear a mask
 *     if asthmatic"); engine bumps it to top.
 *   - Includes UV index (also from same endpoint) — relevant for parents.
 *
 * Privacy: zero PII; only lat/lng we already have are sent.
 */

import type { Widget, WidgetContext } from '../types';
import { swr } from '../../../services/swr';

interface AirPayload {
  aqi: number; // European AQI 0-500 scale
  pm25: number;
  pm10: number;
  uvIndex: number;
  fetchedAt: number;
}

async function fetchAir(lat: number, lng: number): Promise<AirPayload | null> {
  const url = `https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${lat}&longitude=${lng}&current=european_aqi,pm2_5,pm10,uv_index`;
  try {
    const resp = await fetch(url, { cache: 'no-store' });
    if (!resp.ok) return null;
    const data = await resp.json() as { current?: { european_aqi?: number; pm2_5?: number; pm10?: number; uv_index?: number } };
    if (!data.current) return null;
    return {
      aqi: data.current.european_aqi ?? 0,
      pm25: data.current.pm2_5 ?? 0,
      pm10: data.current.pm10 ?? 0,
      uvIndex: data.current.uv_index ?? 0,
      fetchedAt: Date.now(),
    };
  } catch {
    return null;
  }
}

interface AqiBucket { label: string; color: string; emoji: string; tipKey: string; }

function bucketFor(aqi: number): AqiBucket {
  if (aqi < 20) return { label: 'good', color: '#16a34a', emoji: '🟢', tipKey: 'air_tip_good' };
  if (aqi < 40) return { label: 'fair', color: '#84cc16', emoji: '🟢', tipKey: 'air_tip_fair' };
  if (aqi < 60) return { label: 'moderate', color: '#eab308', emoji: '🟡', tipKey: 'air_tip_moderate' };
  if (aqi < 80) return { label: 'poor', color: '#f97316', emoji: '🟠', tipKey: 'air_tip_poor' };
  if (aqi < 100) return { label: 'very_poor', color: '#dc2626', emoji: '🔴', tipKey: 'air_tip_very_poor' };
  return { label: 'extreme', color: '#7c2d12', emoji: '🟣', tipKey: 'air_tip_extreme' };
}

interface UvBucket { label: string; color: string; tipKey: string; }
function uvBucket(uv: number): UvBucket {
  if (uv < 3) return { label: 'low', color: '#16a34a', tipKey: 'uv_tip_low' };
  if (uv < 6) return { label: 'moderate', color: '#eab308', tipKey: 'uv_tip_moderate' };
  if (uv < 8) return { label: 'high', color: '#f97316', tipKey: 'uv_tip_high' };
  if (uv < 11) return { label: 'very_high', color: '#dc2626', tipKey: 'uv_tip_very_high' };
  return { label: 'extreme', color: '#7c2d12', tipKey: 'uv_tip_extreme' };
}

export const AirCard: Widget = {
  id: 'air-card',
  label: {
    en: 'Air & UV',
    fr: 'Air & UV',
    ar: 'الهواء والأشعة',
    es: 'Aire y UV',
    de: 'Luft & UV',
  },
  icon: '🫁',
  defaultVisible: true,
  defaultOrder: 22,
  defaultSize: 'compact',
  visibilityRule: () => {
    // Render only if we have coordinates. The card itself self-styles small
    // when AQI is good — no need to hide entirely (the user might want to
    // glance even on good days when traveling).
    try {
      const raw = localStorage.getItem('aweni_location');
      if (!raw) return false;
      const s = JSON.parse(raw) as { lat?: number | null };
      return typeof s.lat === 'number';
    } catch {
      return false;
    }
  },

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    void size;
    const t = ctx.t;
    const loc = ctx.getLocationService();
    const lat = loc.lat;
    const lng = loc.lng;
    if (lat === null || lng === null) {
      container.innerHTML = '';
      return;
    }

    container.innerHTML = `
      <div data-air-shell style="background:var(--ln-bg-card,var(--card-bg,#fff));border:1px solid var(--ln-border,var(--border,rgba(0,0,0,0.06)));border-radius:16px;padding:14px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
          <span style="font-size:18px;">🫁</span>
          <span style="font-size:13px;font-weight:700;">${escapeHtml(t('widget_air_title') || 'Air & UV')}</span>
          <span data-air-status style="margin-inline-start:auto;font-size:11px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));">…</span>
        </div>
        <div data-air-body>
          <div style="height:42px;background:var(--ln-bg-secondary,var(--bg-secondary,rgba(0,0,0,0.03)));border-radius:8px;" class="skeleton-pulse"></div>
        </div>
      </div>
    `;

    const body = container.querySelector('[data-air-body]') as HTMLElement | null;
    const status = container.querySelector('[data-air-status]') as HTMLElement | null;
    const shell = container.querySelector('[data-air-shell]') as HTMLElement | null;

    const renderPayload = (p: AirPayload): void => {
      if (!body || !shell) return;
      const aq = bucketFor(p.aqi);
      const uv = uvBucket(p.uvIndex);
      // Bump shell border tint when air is poor — visual urgency cue.
      shell.style.borderColor = p.aqi >= 60 ? aq.color : 'var(--ln-border,rgba(0,0,0,0.06))';
      if (status) {
        const dt = new Date(p.fetchedAt);
        status.textContent = dt.toLocaleTimeString(ctx.locale, { hour: '2-digit', minute: '2-digit' });
      }
      const aqLabel = t(`air_label_${aq.label}`) || aq.label;
      const uvLabel = t(`uv_label_${uv.label}`) || uv.label;
      const tip = t(aq.tipKey) || '';

      body.innerHTML = `
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
          <div style="padding:10px;border-radius:10px;background:${aq.color}15;border:1px solid ${aq.color}30;">
            <div style="font-size:10px;color:var(--ln-text-secondary,#6b6657);text-transform:uppercase;letter-spacing:0.4px;">${escapeHtml(t('widget_air_aqi') || 'AQI')}</div>
            <div style="font-size:22px;font-weight:800;color:${aq.color};font-variant-numeric:tabular-nums;line-height:1.1;margin-top:2px;">${aq.emoji} ${p.aqi.toFixed(0)}</div>
            <div style="font-size:11px;font-weight:700;color:${aq.color};text-transform:uppercase;letter-spacing:0.4px;">${escapeHtml(aqLabel)}</div>
          </div>
          <div style="padding:10px;border-radius:10px;background:${uv.color}15;border:1px solid ${uv.color}30;">
            <div style="font-size:10px;color:var(--ln-text-secondary,#6b6657);text-transform:uppercase;letter-spacing:0.4px;">${escapeHtml(t('widget_air_uv') || 'UV')}</div>
            <div style="font-size:22px;font-weight:800;color:${uv.color};font-variant-numeric:tabular-nums;line-height:1.1;margin-top:2px;">☀ ${p.uvIndex.toFixed(0)}</div>
            <div style="font-size:11px;font-weight:700;color:${uv.color};text-transform:uppercase;letter-spacing:0.4px;">${escapeHtml(uvLabel)}</div>
          </div>
        </div>

        <div style="margin-top:8px;display:flex;gap:8px;flex-wrap:wrap;font-size:11px;color:var(--ln-text-secondary,#6b6657);">
          <span>PM2.5: <span style="font-weight:700;font-variant-numeric:tabular-nums;color:var(--ln-text-primary,#1E293B);">${p.pm25.toFixed(0)} µg/m³</span></span>
          <span>PM10: <span style="font-weight:700;font-variant-numeric:tabular-nums;color:var(--ln-text-primary,#1E293B);">${p.pm10.toFixed(0)} µg/m³</span></span>
        </div>

        ${tip ? `<div style="margin-top:8px;padding:8px 10px;border-radius:8px;background:var(--ln-bg-secondary,rgba(0,0,0,0.03));font-size:12px;color:var(--ln-text-primary,#1E293B);">💡 ${escapeHtml(tip)}</div>` : ''}
      `;
    };

    swr<AirPayload>(
      `air_${lat.toFixed(2)}_${lng.toFixed(2)}`,
      () => fetchAir(lat, lng),
      {
        freshTtlMs: 30 * 60 * 1000,
        staleTtlMs: 6 * 60 * 60 * 1000,
        onUpdate: (data) => renderPayload(data),
      },
    ).then((data) => {
      if (data) renderPayload(data);
      else if (body) body.innerHTML = `<div style="font-size:12px;color:var(--ln-text-secondary,#6b6657);">${escapeHtml(t('widget_air_offline') || 'Air data unavailable.')}</div>`;
    }).catch(() => { /* silent */ });
  },
};

function escapeHtml(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
